import CustomButton from "__utilities/components/CustomButton";
import LoadingSpinner from "__utilities/components/LoadingSpinner/LoadingSpinner";
import usePayment from "_data/hooks/payment/usePayment";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { resolveOrderId } from "_navigation/paymentRoutes";
import { SubscriptionStatus } from "__utilities/functions/subscription";

/**
 * Where the customer lands after abandoning checkout (the backend's Payment:CancelUrl).
 *
 * Abandons the order and sends the customer back to plan selection on their account page.
 * Must work opened cold, so the order id falls back to what was stored before we handed the
 * browser to the provider.
 *
 * This only ever abandons an unfinished checkout -- it cannot end a live subscription, so the
 * copy must not suggest anything the customer already has was lost.
 *
 * Nor may it claim nothing was charged without checking. Landing here is not proof the customer
 * backed out: they can complete payment and then hit a stale cancel URL, or the browser can
 * follow it after the payment went through. The backend guards that race -- it asks the provider
 * whether the checkout session has already completed and, if so, leaves the order alone for the
 * confirming webhook -- but it answers 200 either way, so the order state has to be read back to
 * tell the two apart. Telling a customer who has just paid that no amount was drawn is the one
 * outcome worth an extra request to avoid.
 */
const CancelState = {
  Checking: "Checking",
  /** The order really was abandoned; nothing was charged. */
  Abandoned: "Abandoned",
  /** The checkout had in fact completed -- this is a payment, not a cancellation. */
  Paid: "Paid",
};

export default function OnPaymentCancelView() {
  const { abandonOrder, getOrderState } = usePayment();
  const { id } = useParams();
  const navigate = useNavigate();
  const hasStarted = useRef(false);
  const isUnmounted = useRef(false);
  const [state, setState] = useState(CancelState.Checking);

  useEffect(() => {
    if (!hasStarted.current) {
      hasStarted.current = true;

      const orderId = resolveOrderId(id);

      if (!orderId) {
        // Nothing identifies the order, so there is nothing to abandon and nothing to verify.
        // The customer is told the ordinary thing, which is what their action meant.
        setState(CancelState.Abandoned);
      } else {
        abandonOrder(orderId, (abandonSucceeded) => {
          // Read the order back rather than trusting the 200. An order left standing because
          // the provider had already taken the payment is the case this exists for.
          getOrderState(orderId, (success, orderState) => {
            if (isUnmounted.current) {
              return;
            }

            // "Still open" only means "already paid" if the backend actually got as far as
            // evaluating that. An abandon request that never landed leaves the order open too,
            // and reading that as a payment would tell someone who backed out that they are
            // subscribed -- the same lie as the one this guards, pointing the other way. So a
            // failed abandon falls back to the ordinary copy, which invites a retry.
            const isPaid =
              abandonSucceeded === true &&
              success === true &&
              !!orderState?.status &&
              orderState.status !== SubscriptionStatus.Ended;

            setState(isPaid ? CancelState.Paid : CancelState.Abandoned);
          });
        });
      }
    }

    return () => {
      isUnmounted.current = true;
    };
  }, []);

  return (
    <div className="flex flex-col w-full h-full justify-center items-center">
      <div className="flex flex-col w-1/2 h-1/2 p-3 justify-around items-center border border-lightGrey rounded-xl">
        {state === CancelState.Checking && (
          <>
            <p className="text-[34px] font-latoBold">Et øjeblik</p>
            <div className="flex flex-col w-full justify-center items-center gap-y-4">
              <LoadingSpinner />
            </div>
          </>
        )}

        {state === CancelState.Abandoned && (
          <>
            <p className="text-[34px] font-latoBold">Betaling annulleret</p>
            <div className="flex flex-col w-full justify-center items-center">
              <p className="text-[24px] font-latoBold">
                Din betaling er ikke gennemført
              </p>
              <p className="text-standard font-latoBold text-center">
                Der er ikke trukket noget beløb, og dit abonnement er uændret.
                <br />
                Hvis dette var en fejl, bedes du prøve igen eller kontakte vores
                supportteam for assistance.
              </p>
            </div>
            <div className="flex flex-row justify-center items-center gap-x-4">
              <CustomButton
                text={"Vælg abonnement"}
                onClick={() => navigate("/account")}
                styles={"px-3 rounded-xl"}
              />
              <CustomButton
                text={"Gå til startside"}
                onClick={() => navigate("/")}
                styles={"px-3 rounded-xl"}
              />
            </div>
          </>
        )}

        {/* The customer paid after all. Say so, and do not offer to sell them the same thing
            again -- the confirming webhook is what finishes this, on the success page. */}
        {state === CancelState.Paid && (
          <>
            <p className="text-[34px] font-latoBold">
              Din betaling er modtaget
            </p>
            <div className="flex flex-col w-full justify-center items-center">
              <p className="text-standard font-latoBold text-center">
                Din betaling blev gennemført, så dit abonnement er ikke
                annulleret.
                <br />
                Din konto bliver opdateret om kort tid.
              </p>
            </div>
            <CustomButton
              text={"Gå til startside"}
              onClick={() => navigate("/")}
              styles={"px-3 rounded-xl"}
            />
          </>
        )}
      </div>
    </div>
  );
}
